import api, { tokenManager } from './client';
import type {
  LoginCredentials,
  LoginResponse,
  User,
  ChangeSiteResponse,
  ChangePasswordData
} from '../types/auth';

/**
 * Iniciar sesión
 */
export async function login(credentials: LoginCredentials): Promise<LoginResponse> {
  const response = await api.post<LoginResponse>('/auth/login', credentials, {
    skipAuth: true,
  });
  // Guardar tokens
  tokenManager.setTokens(response.accessToken, response.refreshToken);
  return response;
}

/**
 * Cerrar sesión
 */
export async function logout(): Promise<void> {
  try {
    await api.post('/auth/logout');
  } finally {
    tokenManager.clearTokens();
  }
}

/**
 * Cerrar todas las sesiones
 */
export async function logoutAll(): Promise<void> {
  try {
    await api.post('/auth/logout-all');
  } finally {
    tokenManager.clearTokens();
  }
}

/**
 * Obtener usuario actual (validar sesión)
 */
export async function getCurrentUser(): Promise<User> {
  return api.get<User>('/auth/me');
}

/**
 * Refrescar token de acceso
 */
export async function refreshToken(): Promise<{ accessToken: string }> {
  const currentRefreshToken = tokenManager.getRefreshToken();
  if (!currentRefreshToken) {
    throw new Error('No refresh token available');
  }
  const response = await api.post<{ accessToken: string }>(
    '/auth/refresh',
    { refreshToken: currentRefreshToken },
    { skipAuth: true }
  );
  tokenManager.setAccessToken(response.accessToken);
  return response;
}

/**
 * Cambiar sede activa
 */
export async function changeSite(siteId: string): Promise<ChangeSiteResponse> {
  const response = await api.post<ChangeSiteResponse>(`/auth/change-site/${siteId}`);
  // Actualizar token
  tokenManager.setAccessToken(response.accessToken);
  return response;
}

/**
 * Cambiar contraseña del usuario actual
 */
export async function changePassword(data: ChangePasswordData): Promise<{ success: boolean; message: string }> {
  return api.post<{ success: boolean; message: string }>('/auth/change-password', data);
}

/**
 * Verificar si hay sesión activa
 */
export function hasActiveSession(): boolean {
  return tokenManager.hasTokens();
}

export default {
  login,
  logout,
  logoutAll,
  getCurrentUser,
  refreshToken,
  changeSite,
  changePassword,
  hasActiveSession,
};
